import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'
import { useCalibrationCopy } from '../../hooks/useCalibrationCopy'
import BodyFrameMap from './bio-calibration/BodyFrameMap'
import InjuryNodeSheet from './bio-calibration/InjuryNodeSheet'
import {
  buildBioPayloadFromInitialData,
  inventoryNodeCount,
  legacyExperienceToTrainingAge,
  legacyGoalToEnum,
  parseInitialInventory,
  type BodyMapNodeId,
  type BodyProfile,
} from '../../lib/calibration/onboardingConstants'
import { bodyMapNodeLabel } from '../../lib/i18nCatalog'
import { ONBOARDING } from '../../lib/onboarding/formStyles'
import type { OrthopedicInjuryEntry, OrthopedicInjuryInventory } from '../../lib/calibrationTypes'

export type BioCalibrationPayload = NonNullable<ReturnType<typeof buildBioPayloadFromInitialData>>

type BioCalibrationOnboardingProps = {
  initialData?: Record<string, unknown>
  onComplete: (payload: BioCalibrationPayload) => void
}

const BioCalibrationOnboarding = ({ initialData, onComplete }: BioCalibrationOnboardingProps) => {
  const { t } = useTranslation()
  const { trainingGoalOptions, trainingAgeOptions } = useCalibrationCopy()
  const [error, setError] = useState('')
  const [profile, setProfile] = useState<BodyProfile>('front')
  const [inventory, setInventory] = useState<OrthopedicInjuryInventory>(() =>
    parseInitialInventory(initialData?.orthopedic_injury_inventory, String(initialData?.medical_niggles ?? '')),
  )
  const [activeNode, setActiveNode] = useState<BodyMapNodeId | null>(null)

  const flaggedCount = inventoryNodeCount(inventory)

  const profileSummary = useMemo(() => {
    const goal = trainingGoalOptions.find((o) => o.value === legacyGoalToEnum(String(initialData?.training_goal ?? '')))
    const age = trainingAgeOptions.find(
      (o) => o.value === legacyExperienceToTrainingAge(String(initialData?.training_age ?? initialData?.experience_level ?? '')),
    )
    return { dob: String(initialData?.dob ?? ''), goalLabel: goal?.label, ageLabel: age?.label }
  }, [initialData, trainingGoalOptions, trainingAgeOptions])

  const activeNodeLabel = useMemo(() => {
    if (!activeNode) return ''
    return bodyMapNodeLabel(t, activeNode)
  }, [activeNode, t])

  function handleFinish() {
    const payload = buildBioPayloadFromInitialData(initialData ?? {}, inventory)
    if (!payload) {
      setError(t('onboarding.calibration.profile.completeStepsFirst'))
      return
    }
    setError('')
    onComplete(payload)
  }

  function saveNodeEntry(entry: OrthopedicInjuryEntry | null) {
    if (!activeNode) return
    setInventory((prev) => {
      const next = { ...prev }
      if (entry) next[activeNode] = entry
      else delete next[activeNode]
      return next
    })
  }

  return (
    <div className="space-y-4">
      <div className={`${ONBOARDING.card} px-4 py-3`}>
        <p className={KILO.meta}>{t('onboarding.calibration.profile.usingIntakeProfile')}</p>
        <ul className={`mt-2 space-y-1 text-sm ${ONBOARDING.muted}`}>
          <li>
            {t('onboarding.calibration.profile.dob')}: <span className={ONBOARDING.reviewValue}>{profileSummary.dob || '—'}</span>
          </li>
          <li>
            {t('onboarding.calibration.profile.goal')}: <span className={ONBOARDING.reviewValue}>{profileSummary.goalLabel || '—'}</span>
          </li>
          <li>
            {t('onboarding.calibration.profile.trainingAge')}: <span className={ONBOARDING.reviewValue}>{profileSummary.ageLabel || '—'}</span>
          </li>
        </ul>
      </div>

      <div>
        <p className={KILO.meta}>{t('onboarding.calibration.profile.orthopedicRiskGate')}</p>
        <h3 className={ONBOARDING.subheading}>{t('onboarding.calibration.profile.bodyRespectHeading')}</h3>
        <p className={`mt-1 text-sm ${ONBOARDING.muted}`}>{t('onboarding.calibration.profile.bodyRespectSubtitle')}</p>
      </div>

      <div className="flex gap-2">
        {(['front', 'back'] as BodyProfile[]).map((view) => (
          <button
            key={view}
            type="button"
            onClick={() => setProfile(view)}
            className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-wider transition ${
              profile === view ? KILO.pill.active : KILO.pill.inactive
            }`}
          >
            {t(`onboarding.calibration.profile.${view}`)}
          </button>
        ))}
      </div>

      <BodyFrameMap profile={profile} inventory={inventory} activeNode={activeNode} onSelectNode={setActiveNode} />

      <div className={`${ONBOARDING.card} px-4 py-3`}>
        <p className={`text-xs ${ONBOARDING.muted}`}>
          {flaggedCount === 0
            ? t('onboarding.calibration.profile.noNodesFlagged')
            : t('onboarding.calibration.profile.nodesFlagged', { count: flaggedCount })}
        </p>
      </div>

      {error ? <div className={ONBOARDING.error}>{error}</div> : null}

      <button
        type="button"
        onClick={handleFinish}
        className="w-full rounded-2xl bg-kilo-accent py-3 text-sm font-bold text-black"
      >
        {t('onboarding.calibration.profile.saveInjuryMap')}
      </button>

      {activeNode ? (
        <InjuryNodeSheet
          nodeId={activeNode}
          nodeLabel={activeNodeLabel}
          initial={inventory[activeNode] ?? null}
          onSave={saveNodeEntry}
          onClose={() => setActiveNode(null)}
        />
      ) : null}
    </div>
  )
}

export default BioCalibrationOnboarding
